/**
 * 🐛 **Reporting a bug from inside the game** — T174, D12.
 *
 * 🎯 **haugur, 2026-09-07:** *"we should also look into a bug reporting feature
 * on the site for them to use, allow the to report bugs with text and
 * screenshots."*
 *
 * ## 🔴 Why a key in the game and not only a page on the website
 *
 * **Asking somebody to take a screenshot, find it and upload it is where a
 * report stops happening.** ✅ **This grabs the screen itself** — and it attaches
 * what a person never could: **the exact build, where they were standing, the
 * time of day in the world, the size of their window, and the last errors the
 * console logged.**
 *
 * 🔴 **The build is the first question nobody could answer on 2026-09-07**, when
 * a fault was reported that had already been fixed and deployed.
 *
 * ## ⚠️ Plain DOM, deliberately
 *
 * The HUD is SVG and **its drawing is frozen against a hash that only moves when
 * haugur approves a change**. This is an overlay with a real `<textarea>` in it,
 * outside that drawing entirely — **so the frozen hash does not move and this
 * cannot break the screen it is reporting on.**
 */

import { MAX_TEXT } from '@lofi/shared';

/** How many console errors travel with a report. */
const KEEP_ERRORS = 5;

/**
 * 🔴 **The last few console errors, kept as they happen.**
 *
 * ⚠️ **Hooked once, at module load, because an error that matters happened
 * BEFORE the player decided to report it.** Collecting them when the box opens
 * would collect nothing.
 */
const recentErrors: string[] = [];

/** Start listening. ⚠️ Called once; a second call would double every entry. */
export function watchErrors(): void {
  const original = console.error.bind(console);
  console.error = (...args: unknown[]): void => {
    const line = args
      .map((a) => (a instanceof Error ? `${a.name}: ${a.message}` : String(a)))
      .join(' ')
      .slice(0, 300);
    recentErrors.push(line);
    if (recentErrors.length > KEEP_ERRORS) recentErrors.shift();
    original(...args);
  };
  window.addEventListener('error', (e) => {
    recentErrors.push(`window: ${e.message}`.slice(0, 300));
    if (recentErrors.length > KEEP_ERRORS) recentErrors.shift();
  });
}

/** What the game knows and the player does not have to type. */
export interface BugSurroundings {
  readonly at: { x: number; y: number; z: number } | null;
  readonly gameTime: string;
  readonly protocol: number;
}

/**
 * ⏱️ **How the game was running when the picture was taken** — T186.
 *
 * 🎯 **A tester, 2026-09-10:** *"game runs laggy. it has a bit of lag in it."*
 * 🎯 **haugur:** *"i got no further explanations."*
 *
 * 🔴 **"Laggy" hides two different faults with opposite fixes** — the picture
 * stuttering on their machine, and the world arriving late over the wire — and
 * **the word does not say which.** These numbers do.
 *
 * ⚠️ **The player must never be asked to read a number off their own HUD and
 * type it in.** That is exactly the step where a report stops happening.
 */
export interface BugPerformance {
  /** Smoothed frames per second — the steady state. */
  readonly fps: number;
  /** 🔴 The longest single frame in the last second, in milliseconds. */
  readonly worstFrameMs: number;
  /** How many frames in the last second were slow enough to feel. */
  readonly hitches: number;
  /** 🔴 The worst frame in the last half-minute — see `bugreport.ts`. */
  readonly rememberedWorstMs: number;
  /** How many slow frames in that half-minute. */
  readonly rememberedHitches: number;
  /** Render distance, in chunks. The first thing to try when it feels heavy. */
  readonly viewRadius: number;
  /** Chunks asked for and not yet arrived. */
  readonly pending: number;
  /**
   * Median wait from asking for a chunk to it arriving, in milliseconds.
   *
   * ⚠️ **Absent rather than zero when no chunk has arrived yet.** The report
   * then leaves the whole row out. **A *0 ms a chunk* row would claim the world
   * was instant in the one report filed because it was not** — and unlike the
   * frame numbers, which a machine always has, this one genuinely has no value
   * in the first seconds after arriving.
   */
  readonly chunkWaitMs?: number;
  /** The worst such wait in the window. Absent on the same terms. */
  readonly chunkWorstMs?: number;
  /** How many chunk meshes are in the scene — roughly, draw calls. */
  readonly meshes: number;
}

/**
 * 🐛 **The box.** Opens with a picture already taken, sends, and says what
 * happened.
 */
export class BugBox {
  private readonly root: HTMLDivElement;
  private readonly text: HTMLTextAreaElement;
  private readonly status: HTMLParagraphElement;
  private readonly preview: HTMLImageElement;
  private readonly send: HTMLButtonElement;
  private shot: string | null = null;
  private busy = false;
  /**
   * ⏱️ **Sampled when the box OPENS, with the picture** — T186.
   *
   * 🔴 **Not at send, which is where everything else in this file is read.**
   * A player opens the box, types for half a minute and presses Send; by then
   * the game has been sitting behind a modal and *the last second of frames* is
   * a second of nothing happening. **The numbers would describe the typing, not
   * the bug.**
   *
   * ⚠️ **Position and game time are still read at send, and that is correct** —
   * you cannot walk with the box open, so they have not moved. **These can.**
   */
  private perf: BugPerformance | null = null;

  private readonly endpoint: () => string;
  private readonly surroundings: () => BugSurroundings;
  private readonly performance: () => BugPerformance;
  private readonly credential: () => string | undefined;
  private readonly onOpenChanged: (open: boolean) => void;

  /**
   * @param credential The game session this window holds, if it has one — **the
   *   credential, never a name.** See `submit`.
   * @param performance Read at `open`, not at send — see {@link perf}.
   */
  constructor(
    endpoint: () => string,
    surroundings: () => BugSurroundings,
    performance: () => BugPerformance,
    credential: () => string | undefined,
    onOpenChanged: (open: boolean) => void,
  ) {
    /* ⚠️ Assigned rather than declared in the parameter list: this repo builds
       with `erasableSyntaxOnly`, so a parameter property does not compile. */
    this.endpoint = endpoint;
    this.surroundings = surroundings;
    this.performance = performance;
    this.credential = credential;
    this.onOpenChanged = onOpenChanged;
    this.root = document.createElement('div');
    this.root.className = 'bugbox';
    this.root.hidden = true;
    /**
     * ⚠️ **Built element by element, not from a string of HTML.**
     *
     * 🔴 `innerHTML` is refused by the linter and it is right to refuse it: the
     * habit is what turns one interpolated value into a hole. **Nothing here is
     * interpolated today** — and the rule is about the habit, not this line.
     */
    const el = <K extends keyof HTMLElementTagNameMap>(
      tag: K,
      cls: string,
      text?: string,
    ): HTMLElementTagNameMap[K] => {
      const node = document.createElement(tag);
      if (cls) node.className = cls;
      if (text !== undefined) node.textContent = text;
      return node;
    };

    const panel = el('div', 'bugbox-panel');
    panel.append(
      el('h2', '', 'Segðu frá — tell us what went wrong'),
      el(
        'p',
        'bugbox-hint',
        'The first line becomes the title. Your screen, the build and where you are standing are sent with it.',
      ),
    );

    this.text = el('textarea', '');
    this.text.maxLength = MAX_TEXT;
    this.text.placeholder = [
      'The door would not open',
      '',
      'I hit it ten times and nothing happened.',
    ].join(String.fromCharCode(10));
    panel.append(this.text);

    this.preview = el('img', '');
    this.preview.alt = 'what your screen looked like';
    panel.append(this.preview);

    this.status = el('p', 'bugbox-status');
    panel.append(this.status);

    const buttons = el('div', 'bugbox-buttons');
    const cancel = el('button', 'bugbox-cancel', 'Cancel');
    cancel.type = 'button';
    this.send = el('button', 'bugbox-send', 'Send it');
    this.send.type = 'button';
    buttons.append(cancel, this.send);
    panel.append(buttons);
    this.root.append(panel);
    document.body.appendChild(this.root);

    cancel.addEventListener('click', () => {
      this.close();
    });
    this.send.addEventListener('click', () => {
      void this.submit();
    });
    /* ⚠️ Escape closes it, like every other panel. Without this the only way
       out of a box you opened by accident is to reload. */
    this.root.addEventListener('keydown', (e) => {
      /* ⎋ ⚠️ Spoken for — see the same note in `devtools.ts` (T187). 🔴 It
         matters most here: F8 is the most valuable instrument this project
         owns, and a report being written is the worst moment to cover the
         screen with a menu. */
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
      /* The game listens on `document` for E, O and the rest. A player typing
         "open the door" must not open their pack. */
      e.stopPropagation();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /**
   * Show the box.
   *
   * @param shot The screen as a `data:` PNG, or `null` if it could not be
   *   grabbed. ⚠️ **A report with no picture is still worth sending** — the
   *   words and the build are most of the value.
   */
  open(shot: string | null): void {
    this.shot = shot;
    /* ⏱️ T186. Read HERE, beside the picture, because both describe the same
       moment — the one the player is reporting. */
    this.perf = this.performance();
    this.busy = false;
    this.send.disabled = false;
    this.text.value = '';
    this.status.textContent = '';
    if (shot === null) {
      this.preview.hidden = true;
      this.status.textContent = 'The picture could not be taken — send it anyway.';
    } else {
      this.preview.hidden = false;
      this.preview.src = shot;
    }
    this.root.hidden = false;
    this.onOpenChanged(true);
    this.text.focus();
  }

  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    /* Let the picture go; it is megabytes and the box may not open again. */
    this.shot = null;
    this.preview.removeAttribute('src');
    this.onOpenChanged(false);
  }

  private async submit(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.send.disabled = true;
    this.status.textContent = 'Sending…';
    const where = this.surroundings();
    const session = this.credential();
    try {
      const res = await fetch(`${this.endpoint()}/api/bug`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: this.text.value,
          shot: this.shot,
          /**
           * 🧍 **The credential, and NOT the name** — T181.
           *
           * 🔴 **This window knows who it is signed in as** — the `welcome`
           * carries the display name and it is drawn over other people's heads.
           * **Sending it would be the wrong shape**: a browser that names its
           * own reporter can name somebody else's, and the byline would mean
           * nothing the moment anybody wanted it to. **So the game session goes
           * instead and the server looks the name up.**
           *
           * ⚠️ **Absent on dev**, where nobody signs in. The report is still
           * filed and the issue says so in words.
           */
          ...(session === undefined ? {} : { session }),
          context: {
            at: where.at,
            gameTime: where.gameTime,
            protocol: where.protocol,
            agent: navigator.userAgent,
            screen: `${String(window.innerWidth)}×${String(window.innerHeight)}`,
            errors: [...recentErrors],
            /* ⏱️ T186. ⚠️ Omitted rather than sent as zeroes if the box was
               somehow opened without a sample — a report reading *0 fps* would
               be read as a frozen game, which is a worse lie than saying
               nothing. */
            ...(this.perf === null ? {} : { performance: this.perf }),
          },
        }),
      });
      /**
       * 🔴 **READ IT AS TEXT FIRST.** 🎯 haugur, on the first real press of F8:
       * *"It did not go through: Unexpected token '<' … is not valid JSON"* —
       * **which is what `res.json()` says when nginx hands back its own HTML
       * error page.** ⚠️ **The proxy refused the screenshot before the server
       * ever saw it**, and the player was shown a parser message instead of a
       * reason.
       *
       * 👉 **Anything in front of the server can answer, and none of it speaks
       * our JSON.** A reporter that cannot report its own failure is worse than
       * no reporter at all.
       */
      const raw = await res.text();
      let body: { url?: string; number?: number; error?: string } = {};
      try {
        body = JSON.parse(raw) as typeof body;
      } catch {
        body = {};
      }
      if (!res.ok) {
        this.status.textContent =
          body.error ??
          (res.status === 413
            ? /* 🔴 **This should now be unreachable** — since T182 the picture
                 is scaled to 1,600 px and sent as JPEG, a few hundred kilobytes
                 against the several megabytes a full-size PNG ran to. ⚠️ **The
                 message stays anyway.** It was refused twice, and the second
                 fix was applied to the wrong hop; a reporter that cannot report
                 its own failure is worse than no reporter at all. */
              'The picture was too big for the server to accept.'
            : `It did not go through — the server said ${String(res.status)}.`);
        this.busy = false;
        this.send.disabled = false;
        return;
      }
      this.status.textContent = `Thank you — that is bug #${String(body.number ?? 0)}.`;
      window.setTimeout(() => {
        this.close();
      }, 1800);
    } catch (err) {
      this.status.textContent = `It did not go through: ${err instanceof Error ? err.message : String(err)}`;
      this.busy = false;
      this.send.disabled = false;
    }
  }
}
